import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useQuery } from '@tanstack/react-query';
import {
  ShieldExclamationIcon,
  ExclamationTriangleIcon,
  DocumentTextIcon,
  ChartBarIcon,
  EyeIcon,
  StarIcon,
  ClockIcon,
  UserIcon,
  GlobeAltIcon,
  BoltIcon,
  CheckCircleIcon,
  ClipboardDocumentIcon,
  BuildingOfficeIcon,
  LightBulbIcon,
} from '@heroicons/react/24/outline';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { RichTextDisplay } from '@/components/ui/RichTextEditor';
import { cn } from '@/utils/cn';
import { threatIntelligenceService } from '@/services/threat-intelligence.service';
import toast from 'react-hot-toast';

const categoryIcons = {
  cyber: ShieldExclamationIcon,
  physical: ExclamationTriangleIcon,
  personnel: EyeIcon,
  reputation: DocumentTextIcon,
  financial: ChartBarIcon,
};

const threatColors = {
  low: 'bg-success-100 text-success-800 border-success-200 dark:bg-success-900/20 dark:text-success-300 dark:border-success-800',
  medium: 'bg-warning-100 text-warning-800 border-warning-200 dark:bg-warning-900/20 dark:text-warning-300 dark:border-warning-800',
  high: 'bg-orange-100 text-orange-800 border-orange-200 dark:bg-orange-900/20 dark:text-orange-300 dark:border-orange-800',
  critical: 'bg-danger-100 text-danger-800 border-danger-200 dark:bg-danger-900/20 dark:text-danger-300 dark:border-danger-800',
};

const confidenceColors = {
  low: 'text-secondary-600 dark:text-secondary-400',
  medium: 'text-warning-600 dark:text-warning-400',
  high: 'text-success-600 dark:text-success-400',
};

const statusColors = {
  draft: 'bg-secondary-100 text-secondary-800 dark:bg-secondary-800 dark:text-secondary-300',
  review: 'bg-warning-100 text-warning-800 dark:bg-warning-900/20 dark:text-warning-300',
  published: 'bg-success-100 text-success-800 dark:bg-success-900/20 dark:text-success-300',
  archived: 'bg-secondary-100 text-secondary-600 dark:bg-secondary-800 dark:text-secondary-400',
};

export default function ThreatDetails() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [copiedIndicator, setCopiedIndicator] = useState<string | null>(null);

  // Fetch threat assessment data
  const { data: assessment, isLoading, error } = useQuery({
    queryKey: ['threat-assessment', id],
    queryFn: () => {
      if (!id) { throw new Error('No threat ID provided'); }
      return threatIntelligenceService.getThreatAssessmentById(id);
    },
    enabled: !!id,
  });

  const handleCopyIndicator = async (indicator: string | { type: string; value: string }) => {
    try {
      const textToCopy = typeof indicator === 'string' ? indicator : indicator.value;
      await navigator.clipboard.writeText(textToCopy);
      setCopiedIndicator(textToCopy);
      toast.success('Copied to clipboard');
      setTimeout(() => setCopiedIndicator(null), 2000);
    } catch (_error) {
      toast.error('Failed to copy to clipboard');
    }
  };

  const getIndicatorValue = (indicator: string | { type: string; value: string }) => {
    return typeof indicator === 'string' ? indicator : indicator.value;
  };

  const getIndicatorType = (indicator: string | { type: string; value: string }) => {
    return typeof indicator === 'string' ? null : indicator.type;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatDateShort = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  if (isLoading) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="flex flex-col items-center space-y-4">
            <div className="w-12 h-12 border-4 border-primary-200 border-t-primary-600 rounded-full animate-spin" />
            <p className="text-sm text-secondary-600 dark:text-secondary-400">Loading threat assessment...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error ?? !assessment) {
    return (
      <div className="p-6">
        <Card>
          <CardContent className="p-12">
            <div className="text-center">
              <ExclamationTriangleIcon className="w-16 h-16 mx-auto text-danger-500 mb-4" />
              <h2 className="text-xl font-semibold text-secondary-900 dark:text-white mb-2">
                Threat Assessment Not Found
              </h2>
              <p className="text-secondary-600 dark:text-secondary-400 mb-6">
                The threat assessment you&apos;re looking for doesn&apos;t exist or has been removed.
              </p>
              <Button onClick={() => navigate('/security/threats')}>
                Back to Threat Intelligence
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const CategoryIcon = assessment.category ? categoryIcons[assessment.category] : ShieldExclamationIcon;

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        <div className="mb-6">
          <Button
            variant="ghost"
            onClick={() => navigate('/security/threats')}

            className="mb-4"
          >
            Back to Threat Intelligence
          </Button>

          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center space-x-3 mb-2">
                <div className="w-12 h-12 bg-primary-100 dark:bg-primary-900/20 rounded-lg flex items-center justify-center">
                  <CategoryIcon className="w-6 h-6 text-primary-600 dark:text-primary-400" />
                </div>
                <div>
                  <h1 className="text-3xl font-bold text-secondary-900 dark:text-white">
                    {assessment.title}
                  </h1>
                  <p className="text-sm text-secondary-600 dark:text-secondary-400 mt-1">
                    {assessment.id}
                  </p>
                </div>
              </div>

              <div className="flex flex-wrap gap-2 mt-4">
                {assessment.threatLevel && (
                  <span className={cn('px-3 py-1 text-xs font-medium rounded-full border', threatColors[assessment.threatLevel])}>
                    {assessment.threatLevel.toUpperCase()} THREAT
                  </span>
                )}
                {assessment.status && (
                  <span className={cn('px-3 py-1 text-xs font-medium rounded-full', statusColors[assessment.status])}>
                    {assessment.status.toUpperCase()}
                  </span>
                )}
                {assessment.category && (
                  <span className="px-3 py-1 text-xs font-medium bg-secondary-100 text-secondary-700 dark:bg-secondary-800 dark:text-secondary-300 rounded-full">
                    {assessment.category.toUpperCase()}
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      </motion.div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content Column */}
        <div className="lg:col-span-2 space-y-6">
          {/* Description Card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.1 }}
          >
            <Card>
              <CardHeader>
                <CardTitle>Threat Description</CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <RichTextDisplay
                  content={assessment.description}
                  className="text-secondary-700 dark:text-secondary-300 leading-relaxed"
                  emptyMessage="No description provided."
                />
              </CardContent>
            </Card>
          </motion.div>

          {/* Indicators Card */}
          {assessment.indicators && assessment.indicators.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: 0.2 }}
            >
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle>Indicators of Compromise (IOCs)</CardTitle>
                      <CardDescription>
                        Technical indicators associated with this threat
                      </CardDescription>
                    </div>
                    <BoltIcon className="w-5 h-5 text-warning-600 dark:text-warning-400" />
                  </div>
                </CardHeader>
                <CardContent className="p-6">
                  <div className="space-y-2">
                    {assessment.indicators.map((indicator, index) => {
                      const indicatorValue = getIndicatorValue(indicator);
                      const indicatorType = getIndicatorType(indicator);

                      return (
                        <div
                          key={index}
                          className="flex items-center justify-between p-3 bg-secondary-50 dark:bg-secondary-800 rounded-lg group hover:bg-secondary-100 dark:hover:bg-secondary-700 transition-colors"
                        >
                          <div className="flex-1 flex items-center space-x-2 min-w-0">
                            {indicatorType && (
                              <span className="px-2 py-0.5 text-xs font-semibold bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300 rounded flex-shrink-0">
                                {indicatorType}
                              </span>
                            )}
                            <code className="text-sm text-secondary-900 dark:text-white font-mono break-all">
                              {indicatorValue}
                            </code>
                          </div>
                          <button
                            onClick={() => handleCopyIndicator(indicator)}
                            className="ml-3 p-1.5 text-secondary-500 hover:text-primary-600 dark:hover:text-primary-400 transition-colors flex-shrink-0"
                            title="Copy to clipboard"
                          >
                            {copiedIndicator === indicatorValue ? (
                              <CheckCircleIcon className="w-5 h-5 text-success-600" />
                            ) : (
                              <ClipboardDocumentIcon className="w-5 h-5" />
                            )}
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          )}

          {/* Recommendations Card */}
          {assessment.recommendations && assessment.recommendations.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: 0.3 }}
            >
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle>Security Recommendations</CardTitle>
                      <CardDescription>
                        Actions to mitigate this threat
                      </CardDescription>
                    </div>
                    <LightBulbIcon className="w-5 h-5 text-warning-600 dark:text-warning-400" />
                  </div>
                </CardHeader>
                <CardContent className="p-6">
                  <ol className="space-y-3 list-none">
                    {assessment.recommendations.map((recommendation, index) => (
                      <li key={index} className="flex items-start space-x-3">
                        <span className="flex-shrink-0 w-6 h-6 bg-primary-100 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300 rounded-full flex items-center justify-center text-xs font-semibold">
                          {index + 1}
                        </span>
                        <span className="flex-1 text-secondary-700 dark:text-secondary-300 pt-0.5">
                          {recommendation}
                        </span>
                      </li>
                    ))}
                  </ol>
                </CardContent>
              </Card>
            </motion.div>
          )}
        </div>

        {/* Sidebar Column */}
        <div className="lg:col-span-1 space-y-6">
          {/* Metadata Card */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.3, delay: 0.1 }}
          >
            <Card>
              <CardHeader>
                <CardTitle>Assessment Details</CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <div className="space-y-4">
                  {/* Analyst */}
                  <div>
                    <div className="flex items-center space-x-2 text-secondary-600 dark:text-secondary-400 mb-1">
                      <UserIcon className="w-4 h-4" />
                      <span className="text-xs font-medium uppercase">Analyst</span>
                    </div>
                    <p className="text-sm font-medium text-secondary-900 dark:text-white ml-6">
                      {assessment.analyst}
                    </p>
                  </div>

                  {/* Confidence */}
                  {assessment.confidence && (
                    <div>
                      <div className="flex items-center space-x-2 text-secondary-600 dark:text-secondary-400 mb-1">
                        <StarIcon className="w-4 h-4" />
                        <span className="text-xs font-medium uppercase">Confidence Level</span>
                      </div>
                      <p className={cn('text-sm font-semibold ml-6', confidenceColors[assessment.confidence])}>
                        {assessment.confidence.toUpperCase()}
                      </p>
                    </div>
                  )}

                  {/* Created */}
                  <div>
                    <div className="flex items-center space-x-2 text-secondary-600 dark:text-secondary-400 mb-1">
                      <ClockIcon className="w-4 h-4" />
                      <span className="text-xs font-medium uppercase">Created</span>
                    </div>
                    <p className="text-sm text-secondary-900 dark:text-white ml-6">
                      {formatDateShort(assessment.createdAt)}
                    </p>
                  </div>

                  {/* Last Updated */}
                  <div>
                    <div className="flex items-center space-x-2 text-secondary-600 dark:text-secondary-400 mb-1">
                      <ClockIcon className="w-4 h-4" />
                      <span className="text-xs font-medium uppercase">Last Updated</span>
                    </div>
                    <p className="text-sm text-secondary-900 dark:text-white ml-6">
                      {formatDate(assessment.updatedAt)}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          {/* Sources Card */}
          {assessment.sources && assessment.sources.length > 0 && (
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.3, delay: 0.2 }}
            >
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle>Intelligence Sources</CardTitle>
                    <GlobeAltIcon className="w-5 h-5 text-primary-600 dark:text-primary-400" />
                  </div>
                  <CardDescription>
                    {assessment.sources.length} {assessment.sources.length === 1 ? 'source' : 'sources'}
                  </CardDescription>
                </CardHeader>
                <CardContent className="p-6">
                  <div className="space-y-2">
                    {assessment.sources.map((source, index) => (
                      <div
                        key={index}
                        className="flex items-center space-x-2 p-2 bg-secondary-50 dark:bg-secondary-800 rounded-lg"
                      >
                        <div className="w-2 h-2 bg-primary-600 dark:bg-primary-400 rounded-full flex-shrink-0" />
                        <span className="text-sm text-secondary-900 dark:text-white">
                          {source}
                        </span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          )}

          {/* Targeted Assets Card */}
          {assessment.targetedAssets && assessment.targetedAssets.length > 0 && (
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.3, delay: 0.3 }}
            >
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle>Targeted Assets</CardTitle>
                    <BuildingOfficeIcon className="w-5 h-5 text-danger-600 dark:text-danger-400" />
                  </div>
                  <CardDescription>
                    {assessment.targetedAssets.length} {assessment.targetedAssets.length === 1 ? 'asset' : 'assets'} affected
                  </CardDescription>
                </CardHeader>
                <CardContent className="p-6">
                  <div className="flex flex-wrap gap-2">
                    {assessment.targetedAssets.map((asset, index) => (
                      <span
                        key={index}
                        className="px-3 py-1.5 text-xs font-medium bg-danger-50 text-danger-700 dark:bg-danger-900/20 dark:text-danger-300 rounded-lg border border-danger-200 dark:border-danger-800"
                      >
                        {asset}
                      </span>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          )}
        </div>
      </div>
    </div>
  );
}
